import React from 'react';

import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';

import { canDisableMfa } from './stepUpActions';

const remainingCodesMessage = (remaining: number): string => {
  if (remaining === 0) {
    return 'You have no recovery codes left. Regenerate them now so you can still sign in if you lose access to your authenticator app.';
  }
  if (remaining === 1) {
    return 'You have 1 recovery code left. It lets you sign in once if you lose access to your authenticator app.';
  }
  return `You have ${remaining} recovery codes left.`;
};

const MfaEnrollmentStart = ({
  loading,
  enabled,
  required,
  recoveryCodesRemaining,
  onStart,
  onReplaceDevice,
  onRegenerateRecoveryCodes,
  onDisable,
}: {
  loading: boolean;
  enabled: boolean;
  required: boolean;
  recoveryCodesRemaining: number;
  onStart: () => void;
  onReplaceDevice: () => void;
  onRegenerateRecoveryCodes: () => void;
  onDisable: () => void;
}) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Authenticator app
    </Typography>
    {enabled ? (
      <>
        <Alert severity="success" sx={{ marginBottom: 2 }}>
          {required
            ? 'Multi-factor authentication is enabled, and your account requires it. Replace your authenticator device if you are moving to a new phone.'
            : 'Multi-factor authentication is enabled on your account.'}
        </Alert>
        <Alert
          severity={recoveryCodesRemaining ? 'info' : 'warning'}
          sx={{ marginBottom: 2 }}
        >
          {remainingCodesMessage(recoveryCodesRemaining)}
        </Alert>
      </>
    ) : (
      <Typography variant="body1" color="text.secondary" marginBottom={2}>
        Add a second step to your sign in by pairing an authenticator app such as Google
        Authenticator, 1Password or Authy with your Pingwire account.
      </Typography>
    )}
    {enabled ? (
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Button
          variant="outlined"
          onClick={onReplaceDevice}
          disabled={loading}
        >
          Replace authenticator device
        </Button>
        <Button
          variant="outlined"
          onClick={onRegenerateRecoveryCodes}
          disabled={loading}
        >
          Regenerate recovery codes
        </Button>
        {canDisableMfa({ enabled, required }) && (
          <Button
            color="error"
            onClick={onDisable}
            disabled={loading}
          >
            Turn off multi-factor authentication
          </Button>
        )}
      </Stack>
    ) : (
      <Button
        variant="contained"
        onClick={onStart}
        disabled={loading}
      >
        Set up authenticator app
      </Button>
    )}
  </Box>
);

export default MfaEnrollmentStart;
